<script setup>
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUi } from '@/stores/ui.js'
import { useMeta } from '@/stores/meta.js'
import { missingFields, patchFor } from '@/lib/requiredFields.js'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'
import FieldInput from '@/components/form/FieldInput.vue'

/**
 * "Fill in the missing fields" (D-343): a write the company's rules (or a field's definition) would
 * refuse — a stage dragged, an account linked, a phone added to a record without a field it must
 * have — asks for exactly those fields, and goes on with them. One for the whole application
 * (ui.askFields); its answer is what the fields add to the write, or null when cancelled.
 */
const ui = useUi()
const { t } = useI18n()
const meta = useMeta()
const form = reactive({ inputs: {}, refs: {}, errors: {} })
const ask = computed(() => ui.fields)
watch(ask, (a) => {
  if (!a) return
  form.inputs = JSON.parse(JSON.stringify(a.inputs || {}))
  form.inputs.custom_fields = { ...(form.inputs.custom_fields || {}) }
  form.refs = {}
  form.errors = { ...(a.errors || {}) }
})
function set(key, value) {
  form.inputs[key] = value
  // The field filled: its error goes.
  for (const k of Object.keys(form.errors)) if (k === key || (key === 'custom_fields' && k.startsWith('custom_fields.'))) delete form.errors[k]
}
// Still missing, as the rules see the record with what was filled in here.
const still = computed(() => (ask.value ? missingFields(ask.value.entity, form.inputs, meta, { original: ask.value.record }).filter((n) => ask.value.names.includes(n)) : []))
function confirm() {
  if (still.value.length) {
    for (const n of still.value) form.errors[n.startsWith('cf.') ? `custom_fields.${n.slice(3)}` : (meta.fields(ask.value.entity).find((f) => f.name === n)?.input || n)] = [t('missingFields.required')]
    return
  }
  ui.answerFields(patchFor(ask.value.entity, ask.value.names, form.inputs, meta))
}
</script>

<template>
  <UiModal :open="!!ask" :title="ask?.title || $t('missingFields.title')" size="md" data-testid="missing-fields" @close="ui.answerFields(null)">
    <p class="mb-3 text-sm text-muted">{{ ask?.message || $t('missingFields.message') }}</p>
    <div v-if="ask" class="space-y-3">
      <FieldInput v-for="name in ask.names" :key="name" :entity="ask.entity" :name="name" :inputs="form.inputs" :refs="form.refs" :record="ask.record" :effect="{ required: true }" :errors="form.errors" no-comments
                  @set="set" @set-ref="(n, r) => (form.refs[n] = r)" />
    </div>
    <template #footer>
      <UiButton data-testid="missing-fields-cancel" @click="ui.answerFields(null)">{{ $t('common.cancel') }}</UiButton>
      <UiButton variant="primary" data-testid="missing-fields-ok" @click="confirm">{{ $t('missingFields.go') }}</UiButton>
    </template>
  </UiModal>
</template>
